import SessionContext from '@providers/Session/SessionContext';
import { Card, Col, Empty, Row, Spin } from 'antd';
import { Pie } from '@ant-design/plots';
import React, {
  CSSProperties,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import { useGraphColors } from '@hooks/useGraphColors';
import StatusMenu from '@components/StatusMenu';
import { StatusMenuContainer } from '@components/Table/styles';
import { useOnlineDevices } from '@hooks/useOnlineDevices';
import { useIsMobile } from '@hooks/useIsMobile';
import { CreateAlarmButton } from '@components/CreateAlarmButton/CreateAlarmButton';
import { CreateAlarmSteps } from '@components/Insights/CreateAlarm/CreateAlarmSteps';
import rolesConstants from '@utils/rolesConstants';
import RoleChecker from '@components/RoleChecker';
import * as S from './styles';

interface IStyleCss {
  [key: string]: CSSProperties;
}

interface IProps {
  onViewChange: (view: string) => void;
}

interface ICharDataResponse {
  results: {
    series?: {
      columns: string[];
      name: string;
      tags: {
        [key: string]: string;
        macro_regiao: string;
        estado: string;
      };
      values: number[][];
    }[];
    statement_id: number;
  }[];
  totalValue: number;
}

const DEFAULT_FORMDATA = {
  groupByTime: '5m',
  lastTime: '5m',
};

const PieChartWithAlarm: React.FC<{
  title: string;
  data: any[];
  pieConfig: any;
  onOpenModal: () => void;
}> = React.memo(({ title, data, pieConfig, onOpenModal }) => {
  const styles = useMemo(
    () => ({
      charTitle: {
        margin: '0 0 1rem',
        width: '100%',
        textAlign: 'left' as const,
        fontWeight: '600',
      },
    }),
    [],
  );

  return (
    <Card
      title={title}
      extra={
        <RoleChecker role={rolesConstants.INSIGHTS_ALARM_MANAGE_CONFIGURATION}>
          <CreateAlarmButton
            tooltipPosition="leftTop"
            openCreateAlarmModal={onOpenModal}
          />
        </RoleChecker>
      }
      style={styles.charTitle as React.CSSProperties}
    >
      <Pie {...pieConfig} data={data} height={400} className="pie-chart" />
    </Card>
  );
});

const SimcardsStateDashboard: React.FC<IProps> = ({ onViewChange }) => {
  const [openCreateAlarmModal, setOpenCreateAlarmModal] = useState(false);
  const [selectedGraphType, setSelectedGraphType] =
    useState<string>('by-macroregion');

  const isMobile = useIsMobile(591);

  const styles: IStyleCss = useMemo(
    () => ({
      select: {
        width: '15rem',
      },
      spinner: {
        width: '100%',
        padding: '5rem 1rem',
        margin: 16,
      },
      charTitle: {
        margin: '0 0 1rem',
        width: '100%',
        textAlign: 'left',
        fontWeight: '600',
      },
      interval: { stroke: '#fff' },
      notFound: {
        margin: '1rem auto 0',
      },
      questionIcon: { color: '#fff' },
    }),
    [],
  );

  const { t } = useTranslation(['date-picker', 'simcards-online-state']);

  const { statusItems, activeStatusItem, handleStatusCardSelect } =
    useOnlineDevices(onViewChange);

  const { customersSwitch, theme, graphThemeKey } = useContext(SessionContext);
  const graphColors = useGraphColors();
  const colors = graphColors[graphThemeKey];

  const requestParams = useMemo(
    () => ({
      params: {
        lastTime: DEFAULT_FORMDATA?.lastTime,
        groupByTime: DEFAULT_FORMDATA?.groupByTime,
      },
      headers: {
        customerId: customersSwitch?.selected,
      },
    }),
    [customersSwitch],
  );

  const { data: macroRegion, isLoading: macroRegionIsLoading } =
    useSwr<ICharDataResponse>(
      '/service-proxy/dashboard/simcard_online/grafico/regiao/macro_regiao',
      requestParams,
    );

  const { data: state, isLoading: stateIsLoading } = useSwr<ICharDataResponse>(
    '/service-proxy/dashboard/simcard_online/grafico/regiao/estado',
    requestParams,
  );

  const formatValue = useCallback((value: number) => {
    const million = 1000000;
    const thousand = 1000;

    if (value >= million) {
      return `${value / million}mil`;
    }
    if (value >= thousand) {
      return `${value / thousand}k`;
    }
    return value;
  }, []);

  const getCharData = useCallback(
    (tagsName: string, charDataApi?: ICharDataResponse) => {
      const initialValue = 0;
      const maxDecimalPlaces = 2;
      const percentageFactor = 100;

      const listData =
        charDataApi?.results?.[0]?.series?.map(e => {
          const value =
            e?.values?.[e?.values?.length - 1]?.[1] ||
            e?.values?.[e?.values?.length - 2]?.[1] ||
            0;

          return {
            category: e?.tags[tagsName],
            value,
            legendLabel: `${e?.tags[tagsName]}`,
          };
        }) || [];

      const totalValue = Object.values(listData).reduce(
        (sum, card) => sum + card?.value,
        initialValue,
      );

      const formatedData = listData?.map(e => {
        const formattedName = `${e?.category} ${formatValue(e?.value)} : (${(
          (e?.value / totalValue) *
          percentageFactor
        ).toFixed(maxDecimalPlaces)}%)`;

        return {
          ...e,
          type: e?.category,
          label: e?.category,
          name: formattedName,
          value: e?.value,
          percent: e?.value / totalValue,
        };
      });

      return formatedData;
    },
    [formatValue],
  );

  const productData = useMemo(() => {
    const charDataState = getCharData('estado', state);
    const charDataMacroRegion = getCharData('macro_regiao', macroRegion);

    return { charDataState, charDataMacroRegion };
  }, [getCharData, state, macroRegion]);

  const { isLoadingData, hasData } = useMemo(
    () => ({
      isLoadingData: stateIsLoading || macroRegionIsLoading,
      hasData: productData?.charDataMacroRegion?.length,
    }),
    [productData, stateIsLoading, macroRegionIsLoading],
  );

  const handleOpenCreateAlarmModal = useCallback(
    (graphType = 'by-macroregion') => {
      setSelectedGraphType(graphType);
      setOpenCreateAlarmModal(true);
    },
    [],
  );

  const handleCloseCreateAlarmModal = useCallback(() => {
    setOpenCreateAlarmModal(false);
  }, []);

  const tooltipFormatter = useCallback(
    (datum: any) => {
      if (!datum || !datum.type) {
        return { name: '', value: '' };
      }

      const dataItem =
        productData?.charDataState?.find(item => item.type === datum.type) ||
        productData?.charDataMacroRegion?.find(
          item => item.type === datum.type,
        );

      const fullName = dataItem?.name || datum.type || '';

      const match = fullName.match(
        /^(.+?)\s+(\d+(?:\.\d+)?[km]?)\s*:\s*\((.+?)\)$/,
      );

      if (match) {
        const [, name, value, percentage] = match;
        return {
          name: name.trim(),
          value: `${value.trim()} (${percentage.trim()})`,
        };
      }

      const parts = fullName.split(':');
      return {
        name: parts[0]?.trim() || '',
        value: parts[1]?.trim() || '',
      };
    },
    [productData?.charDataState, productData?.charDataMacroRegion],
  );

  const pieConfig = useMemo(
    () =>
      ({
        angleField: 'value',
        colorField: 'type',
        color: colors,
        innerRadius: 0.6,
        label: false,
        legend: {
          layout: isMobile ? 'horizontal' : 'vertical',
          position: isMobile ? 'bottom' : 'right-top',
          itemName: {
            style: {
              fill: theme?.colors?.text || '#000',
            },
          },
        },
        tooltip: {
          formatter: tooltipFormatter,
        },
      } as any),
    [colors, isMobile, theme?.colors?.text, tooltipFormatter],
  );

  if (isLoadingData) return <Spin style={styles.spinner} />;

  return (
    <>
      <Row gutter={[16, 16]} justify="end">
        <Col xs={24}>
          <S.CardContainer
            title={
              <div className="card-title">
                <Row
                  align="middle"
                  justify="space-between"
                  className="card-title-row"
                >
                  <div className="card-title-name">
                    {t('simcards-online-state:titleCard')}
                  </div>
                </Row>
              </div>
            }
          >
            <StatusMenuContainer>
              <StatusMenu
                data={statusItems}
                itemActive={activeStatusItem}
                handleSelectedCard={handleStatusCardSelect}
              />
            </StatusMenuContainer>
            <Row gutter={[16, 16]}>
              {hasData ? (
                <>
                  <Col xs={24} md={24} lg={12}>
                    <PieChartWithAlarm
                      title={t('simcards-online-state:titleMacroregion')}
                      data={productData?.charDataMacroRegion || []}
                      pieConfig={pieConfig}
                      onOpenModal={
                        () => handleOpenCreateAlarmModal('by-macroregion')
                        // eslint-disable-next-line react/jsx-curly-newline
                      }
                    />
                  </Col>
                  <Col xs={24} md={24} lg={12}>
                    <PieChartWithAlarm
                      title={t('simcards-online-state:titleState')}
                      data={productData?.charDataState || []}
                      pieConfig={pieConfig}
                      onOpenModal={() => handleOpenCreateAlarmModal('by-state')}
                    />
                  </Col>
                </>
              ) : (
                <Empty
                  className="empty-container"
                  description={t('simcards-online-state:notFoundMessage')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </Row>
          </S.CardContainer>
        </Col>
      </Row>
      <CreateAlarmSteps
        visible={openCreateAlarmModal}
        onClose={handleCloseCreateAlarmModal}
        preSelectedValues={{
          tipoGrafico: selectedGraphType,
        }}
        readOnlyFields={['tipoGrafico']}
      />
    </>
  );
};

export default SimcardsStateDashboard;
